import {
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_MEASUREMENT_TYPE,
  DELETE_MEASUREMENT_TYPE,
  GET_ALL_MEASUREMENT_TYPE,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { LOOKUP_CATEGORY } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
  toastSuccess,
} from "../../utils/AppFunctions";

export interface IMeasurementType {
  id: string;
  type_name: string;
  turn_around_time: number | null;
  job_completion_time: number | null;
  gender: string;
  is_active: TBitValue;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

export function getMeasurementTypes<T>(
  actionComponent: (item: T) => JSX.Element,
  handleLookupStatus: (item: T) => void,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("type_name"),
      key: "type_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-4",
    },
    {
      name: t("turn_around_time"),
      key: "turn_around_time",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-3",
      assistiveText: t("turn_around_time_title"),
    },
    {
      name: t("job_completion_time"),
      key: "job_completion_time",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-3",
      assistiveText: t("job_completion_time_title"),
    },
    {
      name: t("gender"),
      key: "gender",
      sort: true,
      type: TABLE_FIELD_TYPE.status,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-3",
    },
    {
      name: t("status"),
      key: "is_active",
      sort: false,
      type: TABLE_FIELD_TYPE.switch,
      align: TABLE_FIELD_ALIGN.center,
      checkedValue: "1",
      flex: "col-span-2",
      handleCheck: handleLookupStatus,
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const fetchAllMeasurementType = async (
  setMeasurementType: TReactSetState<IMeasurementType[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: string
) => {
  try {
    const measurementType = await GET_ALL_MEASUREMENT_TYPE(
      pagination,
      search,
      advanceSearch
    );
    if (
      measurementType &&
      measurementType.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setMeasurementType(measurementType.data.items);
      setPagination(measurementType.data.pagination);
    } else {
      setMeasurementType([]);
      sweetAlertError({
        message: measurementType.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "fetchAllMeasurementType");
  }
};

export const deleteMeasurementType = async (id: string) => {
  try {
    const deletedData = await DELETE_MEASUREMENT_TYPE(id);
    if (deletedData && deletedData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deletedData.message });
      return true;
    } else {
      sweetAlertError({
        message: deletedData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteMeasurementType");
    return false;
  }
};

export const activeInactiveMeasurementType = async (
  id: string,
  values: TBitValue
) => {
  try {
    const activeInactiveData = await ACTIVE_INACTIVE_MEASUREMENT_TYPE(id, {
      is_active: values,
    });
    if (
      activeInactiveData &&
      activeInactiveData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(activeInactiveData.message);
      return true;
    } else {
      sweetAlertError({
        message: activeInactiveData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "activeInactiveMeasurementType");
    return false;
  }
};

export const getGenderDdl = async (
  setGender: TReactSetState<IOption<string>[]>,
  selectedGanderValue?: string
) => {
  try {
    const payload = {
      entity: "sts_lookups",
      data_value: "data_value",
      display_value: "display_value",
      filters: [
        {
          field: "lookup_category",
          value: LOOKUP_CATEGORY.Gender,
          condition: "eq",
        },
      ],
      ...(selectedGanderValue && {
        lookup_values: [selectedGanderValue],
        lookup_category: LOOKUP_CATEGORY.Gender,
      }),
    };
    const genderDDLList = await GLOBAL_DROP_DOWN(payload);
    if (genderDDLList && genderDDLList.code === DEFAULT_STATUS_CODE_SUCCESS) {
      if (genderDDLList.data) {
        setGender(genderDDLList.data);
      } else {
        setGender([]);
      }
    } else {
      toastError(genderDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getGenderDdl");
  }
};
